/**
 * Utilitaires pour la generation de fichiers DTA (CCSS SECUline)
 *
 * Format CCSS :
 * - Encodage : ASCII (pas UTF-8)
 * - Separateur : point-virgule (;)
 * - Fin de ligne : CR+LF (0x0D 0x0A)
 * - Montants : en centimes, sans separateur de milliers
 * - Dates : YYYYMMDD ou YYYYMM selon le champ
 */

const CRLF = '\r\n'
const SEP = ';'

/**
 * Encode un fichier DTA a partir de lignes de champs.
 * Chaque ligne est un tableau de valeurs string.
 */
export function encodeDTA(lines: string[][]): string {
  return lines.map(fields => fields.join(SEP)).join(CRLF) + CRLF
}

/**
 * Convertit un fichier DTA en Blob telechargebale.
 */
export function dtaToBlob(content: string): Blob {
  // Encode en ASCII (latin1) pour compatibilite CCSS
  const encoder = new TextEncoder()
  const bytes = encoder.encode(content)
  return new Blob([bytes], { type: 'text/plain;charset=iso-8859-1' })
}

/**
 * Telecharge un fichier DTA.
 */
export function downloadDTA(content: string, filename: string): void {
  const blob = dtaToBlob(content)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ─── Formatage champs ────────────────────────────────────────────

/** Formate une date YYYYMMDD */
export function formatDateDTA(isoDate: string): string {
  return isoDate.replace(/-/g, '').slice(0, 8)
}

/** Formate une periode YYYYMM */
export function formatPeriodDTA(year: number, month: number): string {
  return `${year}${String(month).padStart(2, '0')}`
}

/** Formate un montant en centimes (string sans separateur) */
export function formatAmountDTA(cents: number): string {
  return String(Math.abs(cents))
}

/** Formate un nombre de jours (2 chiffres) */
export function formatDaysDTA(days: number): string {
  return String(days).padStart(2, '0')
}

/** Formate un matricule CCSS (13 chiffres) */
export function formatMatriculeDTA(matricule: string): string {
  return matricule.replace(/\s/g, '').padStart(13, '0')
}

/** Formate un numero employeur CCSS (10 chiffres) */
export function formatEmployerNumberDTA(num: string): string {
  return num.replace(/\s/g, '').padStart(10, '0')
}

/**
 * Supprime les accents et caracteres speciaux (ASCII only).
 * Les fichiers CCSS n'acceptent que l'ASCII.
 */
export function toAscii(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\x20-\x7E]/g, '')
}
